import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTrashAlt } from "react-icons/fa";

import Select from "react-select";
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import {
  applyClassToCharacters,
  customOption,
  isUrdu
} from "../../../../constants/const";
import { GetLanguageString } from "../../../helper/Components";
import { GetCustomStyles } from "../../../helper/Method";
import { getMinMaxDates } from "../../fiscalYearHelper";
import { AmanatAccountBalanceCard, CashAccountBalanceCard } from "../../BalanceCards";

const AmanatToCashAccountForm = ({
  setValues,
  values,
  errors,
  fundAccountBalances,
  handleInputChange,
  handleDateChange,
  handleBankAccountChange,
  isEdit = false,
  selectedBankAccount,
  unitBankAccounts,
  handleAddNew,
  showRowsTable,
  showExtraFile,
  handleChangeTableData,
  rows,
  addRow,
  removeRow,
  disableFileForFuture = false,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* BALANCE CARDS */}
      <Row style={{ marginBottom: "20px" }}>
        <AmanatAccountBalanceCard
          balance={fundAccountBalances?.amanatAccountBalance}
          loading={!fundAccountBalances || fundAccountBalances.amanatAccountBalance === undefined}
          colProps={{ xs: "6", md: "6", xl: "6", className: "mb-3" }}
        />
        <CashAccountBalanceCard
          balance={fundAccountBalances?.cashAccountBalance}
          loading={!fundAccountBalances || fundAccountBalances.cashAccountBalance === undefined}
          colProps={{ xs: "6", md: "6", xl: "6", className: "mb-3" }}
        />
      </Row>
      <Row>
        {/* Transfer Amount */}
        <Col md="6">
          <FormGroup>
            <Label className="col-form-label">
              <GetLanguageString props="funds_add_fund_amount" />
            </Label>
            <Input
              type="text"
              name="transferAmount"
              placeholder={t("funds_add_fund_amount")}
              value={values.transferAmount}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  handleInputChange({
                    target: { name: "transferAmount", value: val },
                  });
                }
              }}
              invalid={!!errors.transferAmount}
            />
            {errors.transferAmount && (
              <FormFeedback>
                <h4>{errors.transferAmount}</h4>
              </FormFeedback>
            )}
          </FormGroup>
        </Col>

        {/* Deposit Date */}
        <Col md="6">
          <FormGroup>
            <Label className="col-form-label">
              <GetLanguageString props="dashboard_event_page_date" />
            </Label>
            <DatePicker
              selected={values.requestDate}
              onChange={handleDateChange}
              className={`form-control ${errors.requestDate ? "is-invalid" : ""
                }`}
              dateFormat="dd/MM/yyyy"
              minDate={getMinMaxDates(values.requestDate).minDate}
              maxDate={getMinMaxDates(values.requestDate).maxDate}
            />
            {errors.requestDate && (
              <div className="invalid-feedback d-block">
                <h4>{errors.requestDate}</h4>
              </div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label className="col-form-label">
              <GetLanguageString props="fund_bank_account" />
            </Label>

            <div style={{ display: "flex", gap: "8px" }}>
              {/* Select */}
              <div style={{ flex: 1 }}>
                <Select
                  components={{
                    Option: customOption,
                  }}
                  options={unitBankAccounts}
                  value={{
                    value: selectedBankAccount?.value,
                    label: selectedBankAccount?.label
                      ? applyClassToCharacters(selectedBankAccount?.label, "ur")
                      : "",
                  }}
                  onChange={handleBankAccountChange}
                  placeholder="Select bank account"
                  classNamePrefix="select"
                />
              </div>

              {/* Small Button */}
              <Button
                color="primary"
                size="sm"
                onClick={() => handleAddNew(true)}
                style={{
                  height: "38px", // matches Select height
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 12px", // small compact button
                }}
              >
                <FaPlus
                  color="#f7f7f7ff"
                  size={11}
                  style={{ marginRight: 5, marginBottom: 1 }}
                />
                {t("tutorial_add_category")}
              </Button>
            </div>
          </FormGroup>
        </Col>

        {/* Memo */}
        <Col md={isEdit ? "6" : "6"}>
          <FormGroup>
            <Label className="col-form-label">
              <GetLanguageString props="transactions_memo" />
            </Label>
            <Input
              type="textarea"
              name="memo"
              placeholder="Memo"
              className={isUrdu(values?.memo) ? "ur " : ""}
              value={values?.memo || ""}
              onChange={handleInputChange}
              invalid={!!errors?.memo}
            />
            {errors?.memo && (
              <h4 className="text-error">{errors.memo}</h4>
            )}
          </FormGroup>
        </Col>
        {showExtraFile && (
          <Col md="6">
            <FormGroup>
              <Label className="col-form-label">
                <GetLanguageString props="transactions_shura_approval_attachment" />
              </Label>
              <Input
                type="file"
                name="extraFile"
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    extraFile: e.target.files[0],
                  }))
                }
                className={`form-control ${errors.extraFile ? "is-invalid" : ""
                  }`}
              />
              {errors.extraFile && (
                <div className="invalid-feedback d-block">
                  <h4>{errors.extraFile}</h4>
                </div>
              )}
            </FormGroup>
          </Col>
        )}
      </Row>

      {showRowsTable && (
        <>
          <hr />
          <Row>
            <div className="ibox">
              <div className="ibox-title">
                <h5>{<GetLanguageString props="sidebar_funds_expenses" />}</h5>
                <Button
                  className={GetCustomStyles().btn_style}
                  color="white"
                  size="xs"
                  onClick={addRow}
                >
                  <FaPlus
                    color="#18A689"
                    size={13}
                    style={{ marginRight: 5, marginBottom: 1 }}
                  />
                  {<GetLanguageString props="tutorial_add_category" />}
                </Button>
              </div>

              <div className="ibox-content">
                <Row>
                  <Col md="12">
                    <Table bordered responsive>
                      <thead>
                        <tr>
                          <th>
                            {
                              <GetLanguageString props="sidebar_funds_expense" />
                            }
                          </th>
                          <th>
                            {
                              <GetLanguageString props="funds_add_fund_amount" />
                            }
                          </th>
                          <th>
                            {
                              <GetLanguageString props="dashboard_event_page_date" />
                            }
                          </th>
                          <th>
                            {
                              <GetLanguageString props="transactions_expense_type" />
                            }
                          </th>
                          <th>
                            {
                              <GetLanguageString props="transactions_expense_receipt" />
                            }
                          </th>
                          <th style={{ width: "40px" }}></th>
                        </tr>
                      </thead>

                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i}>
                            <td>
                              <Input
                                type="text"
                                value={row.description}
                                onChange={(e) =>
                                  handleChangeTableData(
                                    i,
                                    "description",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter description"
                                className={
                                  errors[`row_${i}_description`]
                                    ? "is-invalid"
                                    : ""
                                }
                              />
                              {errors[`row_${i}_description`] && (
                                <div className="invalid-feedback d-block">
                                  <h4>{errors[`row_${i}_description`]}</h4>
                                </div>
                              )}
                            </td>

                            <td>
                              <Input
                                type="text"
                                value={row.amount}
                                onChange={(e) => {
                                  const val = e.target.value;

                                  // Allow only digits (empty allowed)
                                  if (/^\d*$/.test(val)) {
                                    handleChangeTableData(i, "amount", val);
                                  }
                                }}
                                placeholder="0"
                                className={
                                  errors[`row_${i}_amount`] ? "is-invalid" : ""
                                }
                              />

                              {errors[`row_${i}_amount`] && (
                                <div className="invalid-feedback d-block">
                                  <h4>{errors[`row_${i}_amount`]}</h4>
                                </div>
                              )}
                            </td>

                            <td>
                              <DatePicker
                                selected={
                                  row.date ? new Date(row.date) : new Date()
                                }
                                onChange={(date) =>
                                  handleChangeTableData(i, "date", date)
                                }
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                                minDate={
                                  row.expenseType === "future"
                                    ? new Date()
                                    : getMinMaxDates(row.date).minDate
                                }
                                maxDate={
                                  row.expenseType === "past"
                                    ? new Date()
                                    : getMinMaxDates(row.date).maxDate
                                }
                              />
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ gap: "25px", height: "100%" }}
                              >
                                {/* Past */}
                                <label
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ cursor: "pointer" }}
                                >
                                  <Input
                                    type="radio"
                                    name={`expenseType_${i}`}
                                    value="past"
                                    checked={row.expenseType === "past"}
                                    onChange={(e) =>
                                      handleChangeTableData(
                                        i,
                                        "expenseType",
                                        e.target.value
                                      )
                                    }
                                    style={{ marginRight: "6px" }}
                                  />
                                  <span>
                                    {
                                      <GetLanguageString props="transactions_past" />
                                    }
                                  </span>
                                </label>

                                {/* Future */}
                                <label
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ cursor: "pointer" }}
                                >
                                  <Input
                                    type="radio"
                                    name={`expenseType_${i}`}
                                    value="future"
                                    checked={row.expenseType === "future"}
                                    onChange={(e) =>
                                      handleChangeTableData(
                                        i,
                                        "expenseType",
                                        e.target.value
                                      )
                                    }
                                    style={{ marginRight: "6px" }}
                                  />
                                  <span>
                                    {
                                      <GetLanguageString props="transactions_future" />
                                    }
                                  </span>
                                </label>
                              </div>
                            </td>

                            <td>
                              <Input
                                type="file"
                                onChange={(e) =>
                                  handleChangeTableData(
                                    i,
                                    "file",
                                    e.target.files[0]
                                  )
                                }
                                disabled={
                                  disableFileForFuture &&
                                  row.expenseType === "future"
                                }
                                className={
                                  errors[`row_${i}_file`] ? "is-invalid" : ""
                                }
                              />

                              {errors[`row_${i}_file`] && (
                                <div className="invalid-feedback d-block">
                                  <h4>{errors[`row_${i}_file`]}</h4>
                                </div>
                              )}
                            </td>

                            {/* <td>
                              <Input
                                type="file"
                                onChange={(e) =>
                                  handleChangeTableData(i, "file", e.target.files[0])
                                }
                              />
                            </td> */}
                            <td style={{ width: "40px", textAlign: "center" }}>
                              {rows.length > 1 && (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeRow(i)}
                                  style={{ padding: "5px 8px" }}
                                >
                                  <FaTrashAlt />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </div>
            </div>
          </Row>
        </>
      )}
    </>
  );
};
export default AmanatToCashAccountForm;
